import React, { useEffect, useState } from "react";
import { Plane, Plus, Edit3, Trash2, X, Save, Info } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const EMPTY = {
  name: "",
  coin: "WART",
  miner: "bzminer",
  pool_url: "stratum+tcp://pool.warthog.network:4444",
  wallet: "",
  worker_name: "%RIG_NAME%",
  extra_args: "",
};

// Fallback statico se /api/miners è irraggiungibile (es. backend in restart).
// La struttura è la stessa che il backend espone, ma con campo `note` invece
// di `note_it` e `coin` invece di `default_coin` per retro-compat con la UI.
const FALLBACK_MINER_PRESETS = {
  bzminer: {
    name: "bzminer",
    coin: "WART",
    pool_url: "stratum+tcp://pool.warthog.network:4444",
    extra_args: "",
    algos: ["warthog"],
    requires: ["NVIDIA / AMD / CPU"],
    supports_cpu: true,
    label: "bzminer · Warthog / Janushash",
    note: "Multi-GPU NVIDIA+AMD+CPU. OC live via NVML, niente restart su cambio OC.",
  },
  lpminer: {
    name: "lpminer",
    coin: "PRL",
    pool_url: "stratum+tcp://prl-eu.kryptex.network:7048",
    extra_args: "",
    algos: ["pearl"],
    requires: ["RTX 2000+ (sm_75+)"],
    supports_cpu: false,
    label: "lpminer · Pearl / PearlHash",
    note: "Solo NVIDIA RTX 2060+. NON funziona su RTX 4070/4080/4090 (illegal memory access).",
  },
  srbminer: {
    name: "srbminer",
    coin: "PRL",
    pool_url: "prl-eu.kryptex.network:7048",
    extra_args: "--disable-cpu",
    algos: ["pearlhash", "kaspa", "karlsenhashv2", "ravencoin", "ethash", "etchash", "alephium", "ironfish", "nexa"],
    requires: ["NVIDIA / AMD"],
    supports_cpu: false,
    label: "SRBMiner · multi-algo (PRL, KAS, RVN, …)",
    note: "Multi-algo NVIDIA+AMD. Per Kryptex: pool senza stratum+tcp://, wallet auto-formattato come WALLET.WORKER.",
  },
};

// Trasforma il manifest serializzato dal backend nel formato usato dalla UI.
const manifestToPreset = (m) => ({
  name: m.name,
  coin: m.default_coin || "",
  pool_url: m.default_pool || "",
  extra_args: m.default_extra_args || "",
  algos: Array.isArray(m.algos) && m.algos.length ? m.algos : [""],
  requires:
    m.supports_cpu ? ["NVIDIA / AMD / CPU"] :
    m.supports_amd ? ["NVIDIA / AMD"] :
    ["NVIDIA"],
  supports_cpu: !!m.supports_cpu,
  supports_amd: !!m.supports_amd,
  label: m.label,
  note: m.note_it || "",
  incompatible_gpus: m.incompatible_gpus || [],
});


function FormModal({ open, onClose, initial, onSaved, minerPresets }) {
  const [data, setData] = useState(initial || EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setData(initial || EMPTY);
  }, [open, initial]);

  if (!open) return null;

  const update = (k, v) => setData((s) => ({ ...s, [k]: v }));

  // Quando l'utente cambia il miner, applica i preset (pool/coin) solo se i
  // campi attuali sono vuoti o coincidono col preset del miner precedente.
  const handleMinerChange = (newMiner) => {
    const prev = minerPresets[data.miner] || {};
    const next = minerPresets[newMiner] || {};
    setData((s) => ({
      ...s,
      miner: newMiner,
      coin: !s.coin || s.coin === prev.coin ? next.coin : s.coin,
      pool_url: !s.pool_url || s.pool_url === prev.pool_url ? next.pool_url : s.pool_url,
    }));
  };

  const preset = minerPresets[data.miner] || minerPresets.bzminer || FALLBACK_MINER_PRESETS.bzminer;

  const save = async () => {
    if (!data.name || !data.wallet || !data.pool_url) {
      toast.error("Nome, wallet e pool URL sono obbligatori");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: data.name,
        coin: data.coin,
        miner: data.miner,
        pool_url: data.pool_url,
        wallet: data.wallet,
        worker_name: data.worker_name || "%RIG_NAME%",
        extra_args: data.extra_args || "",
      };
      if (data.id) {
        await api.put(`/flight-sheets/${data.id}`, payload);
      } else {
        await api.post("/flight-sheets", payload);
      }
      toast.success(data.id ? "Flight sheet aggiornata" : "Flight sheet creata");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bento p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="fs-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading font-bold text-xl">
            {data.id ? "Modifica Flight Sheet" : "Nuova Flight Sheet"}
          </h3>
          <button onClick={onClose} data-testid="fs-modal-close" className="text-[#A1A1AA] hover:text-[#FAFAFA]">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-4">
          {/* Miner selector — first, so changing it auto-fills pool/coin */}
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA] block mb-1.5">
              Miner
            </span>
            <select
              value={data.miner}
              onChange={(e) => handleMinerChange(e.target.value)}
              data-testid="fs-input-miner"
              className="w-full bg-[#0B0B0F] border border-[#27272A] rounded-sm px-3 py-2 text-sm font-mono outline-none focus:border-[#10B981]"
            >
              {Object.entries(minerPresets).map(([key, p]) => (
                <option key={key} value={key}>{p.label}</option>
              ))}
            </select>
            <div
              data-testid={`fs-miner-note-${data.miner}`}
              className="mt-2 flex items-start gap-2 text-[11px] text-[#A1A1AA] bg-[#10B981]/5 border border-[#10B981]/20 rounded-sm px-2.5 py-1.5"
            >
              <Info className="w-3 h-3 mt-0.5 text-[#10B981] shrink-0" />
              <span>{preset.note}</span>
            </div>
          </label>

          {[
            { k: "name", label: "Nome", placeholder: "Es: Warthog Default" },
            { k: "coin", label: "Coin", placeholder: "WART" },
            { k: "pool_url", label: "Pool URL", placeholder: "stratum+tcp://pool.warthog.network:4444" },
            { k: "wallet", label: "Wallet Address", placeholder: data.miner === "lpminer" ? "krxXXXXXXX (Kryptex username)" : "warthog1q..." },
            { k: "worker_name", label: "Worker Name", placeholder: "%RIG_NAME% (sostituito al volo)" },
          ].map(({ k, label, placeholder }) => (
            <label key={k} className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA] block mb-1.5">
                {label}
              </span>
              <input
                value={data[k]}
                onChange={(e) => update(k, e.target.value)}
                placeholder={placeholder}
                data-testid={`fs-input-${k}`}
                className="w-full bg-[#0B0B0F] border border-[#27272A] rounded-sm px-3 py-2 text-sm font-mono outline-none focus:border-[#10B981]"
              />
            </label>
          ))}
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA] block mb-1.5">
              Extra args (passati al miner)
            </span>
            <textarea
              value={data.extra_args}
              onChange={(e) => update("extra_args", e.target.value)}
              rows={2}
              placeholder={data.miner === "lpminer" ? "(opzionale, normalmente vuoto)" : "--algo karlsenhashv2 --no-watchdog"}
              data-testid="fs-input-extra-args"
              className="w-full bg-[#0B0B0F] border border-[#27272A] rounded-sm px-3 py-2 text-sm font-mono outline-none focus:border-[#10B981] resize-none"
            />
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-mono uppercase tracking-wider text-[#A1A1AA] hover:text-[#FAFAFA]"
          >
            Annulla
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="fs-save-btn"
            className="bg-[#10B981] hover:bg-[#34D399] disabled:opacity-50 text-[#09090B] font-bold rounded-sm px-4 py-2 text-xs uppercase tracking-[0.15em] flex items-center gap-1.5"
          >
            <Save className="w-3.5 h-3.5" /> {saving ? "Salvo…" : "Salva"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function FlightSheetsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [minerPresets, setMinerPresets] = useState(FALLBACK_MINER_PRESETS);

  const load = async () => {
    try {
      const { data } = await api.get("/flight-sheets");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  // Carica i miner disponibili dinamicamente dal backend (plugin scan).
  // Fallback ai preset statici se l'endpoint è irraggiungibile.
  const loadMinerPresets = async () => {
    try {
      const { data } = await api.get("/miners");
      if (Array.isArray(data) && data.length) {
        const presets = {};
        data.forEach((m) => { presets[m.name] = manifestToPreset(m); });
        setMinerPresets(presets);
      }
    } catch (e) {
      // silently keep fallback
      console.warn("/api/miners unreachable, using fallback presets", e);
    }
  };

  useEffect(() => { load(); loadMinerPresets(); }, []);

  const remove = async (fs) => {
    if (!confirm(`Eliminare la flight sheet "${fs.name}"?`)) return;
    try {
      await api.delete(`/flight-sheets/${fs.id}`);
      toast.success("Flight sheet eliminata");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#10B981] mb-1.5">
            // Miner Configurations
          </div>
          <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight">
            Flight Sheets
          </h1>
          <p className="text-sm text-[#A1A1AA] mt-1">
            Config riutilizzabili (coin, pool, wallet, miner) da assegnare ai rig.
          </p>
        </div>
        <button
          onClick={() => { setEditing(null); setOpen(true); }}
          data-testid="fs-new-btn"
          className="flex items-center gap-2 bg-[#10B981] hover:bg-[#34D399] text-[#09090B] font-bold rounded-sm px-4 py-2 text-xs uppercase tracking-[0.15em]"
        >
          <Plus className="w-4 h-4" /> Nuova
        </button>
      </div>

      {loading ? (
        <div className="font-mono text-sm text-[#A1A1AA]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bento p-12 text-center">
          <Plane className="w-10 h-10 text-[#A1A1AA] mx-auto mb-3" />
          <h3 className="font-heading font-bold text-xl mb-2">Nessuna flight sheet</h3>
          <p className="text-sm text-[#A1A1AA] mb-5">
            Crea la prima config per assegnarla ai rig (es. Warthog + bzminer).
          </p>
          <button
            onClick={() => { setEditing(null); setOpen(true); }}
            data-testid="fs-empty-new-btn"
            className="inline-flex items-center gap-2 bg-[#10B981] hover:bg-[#34D399] text-[#09090B] font-bold rounded-sm px-4 py-2 text-xs uppercase tracking-[0.15em]"
          >
            <Plus className="w-4 h-4" /> Crea Flight Sheet
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 stagger-fade">
          {items.map((fs) => (
            <div key={fs.id} className="bento p-5" data-testid={`fs-card-${fs.name}`}>
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="min-w-0">
                  <div className="font-heading font-bold text-lg truncate">{fs.name}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#10B981] mt-0.5">
                    {fs.coin} · {fs.miner}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => { setEditing(fs); setOpen(true); }}
                    data-testid={`fs-edit-${fs.name}`}
                    className="p-1.5 text-[#A1A1AA] hover:text-[#FAFAFA] hover:bg-[#27272A] rounded-sm"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => remove(fs)}
                    data-testid={`fs-delete-${fs.name}`}
                    className="p-1.5 text-[#A1A1AA] hover:text-[#EF4444] hover:bg-[#EF4444]/10 rounded-sm"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <div className="space-y-2 text-xs font-mono">
                <div>
                  <div className="text-[#A1A1AA] text-[10px] uppercase tracking-[0.15em]">Pool</div>
                  <div className="text-[#FAFAFA] break-all">{fs.pool_url}</div>
                </div>
                <div>
                  <div className="text-[#A1A1AA] text-[10px] uppercase tracking-[0.15em]">Wallet</div>
                  <div className="text-[#FAFAFA] break-all">{fs.wallet}</div>
                </div>
                <div>
                  <div className="text-[#A1A1AA] text-[10px] uppercase tracking-[0.15em]">Worker</div>
                  <div className="text-[#FAFAFA]">{fs.worker_name}</div>
                </div>
                {fs.extra_args && (
                  <div>
                    <div className="text-[#A1A1AA] text-[10px] uppercase tracking-[0.15em]">Args</div>
                    <div className="text-[#FAFAFA] break-all">{fs.extra_args}</div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <FormModal open={open} onClose={() => setOpen(false)} initial={editing} onSaved={load} minerPresets={minerPresets} />
    </div>
  );
}
